import { FC, ReactNode } from "react";
import { observer } from "mobx-react";
import { LiquidateCompanyForm } from "./liquidateCompanyDomain";
import { VBox } from "@/components-v2/Box";
import { Alert } from "@/components-v2/Alert";
import { BooleanFieldModel } from "@/common/commonForms";
import { RadioButtonGroup } from "@/components-v2/RadioButton";
import { TFunction, TMessage } from "@/i18n/tt";
import { formatDate } from "@/i18n/dateFormat";
import { Language } from "@/i18n/languages";
import { useTranslation } from "@/stores/RootStoreContext";

const Question: FC<{
  question: string;
  field: BooleanFieldModel;
  name: string;
  noLabel: string;
  isLocked: boolean;
  isYesDisabled?: boolean;
  error?: TMessage;
  finding?: { type: "success" | "error"; content: ReactNode };
}> = observer(({ question, field, name, noLabel, isLocked, isYesDisabled, error, finding }) => {
  const { t } = useTranslation();

  return (
    <VBox>
      <div>{question}</div>
      <RadioButtonGroup
        name={name}
        horizontal
        selected={field.value ?? undefined}
        options={[true, false]}
        idFn={(answer) => String(answer)}
        labelFn={(answer) => (answer ? t("yes") : noLabel)}
        disabledFn={(answer) => answer && Boolean(isYesDisabled)}
        onChange={(_, answer) => field.setValue(answer)}
        disabled={isLocked}
      />
      {error && <Alert type={"error"} icon alignItems={"start"} message={<>{t(error)}</>} />}
      {(field.value !== false || finding?.type === "error") && finding && (
        <Alert type={finding.type} icon alignItems={"start"} message={<>{finding.content}</>} />
      )}
    </VBox>
  );
});

function creditorFinding(
  form: LiquidateCompanyForm,
  locale: Language,
  t: TFunction,
): { type: "success" | "error"; content: ReactNode } | undefined {
  if (!form.brRegisteredDate || !form.creditorNoticeExpiryDate) {
    return undefined;
  }
  const dates = {
    registeredDate: formatDate(locale, form.brRegisteredDate),
    expiryDate: formatDate(locale, form.creditorNoticeExpiryDate),
  };
  if (!form.isCreditorPeriodRunning) {
    return {
      type: "success",
      content: t("decisions.liquidateCompany.isCreditorNoticeExpired.found", dates),
    };
  }
  return {
    type: "error",
    content: (
      <div>
        <p className={"font-semibold"}>
          {t("decisions.liquidateCompany.isCreditorNoticeExpired.running.title")}
        </p>
        <p>{t("decisions.liquidateCompany.isCreditorNoticeExpired.running.text", dates)}</p>
      </div>
    ),
  };
}

export const LiquidateCompanyView: FC<{ form: LiquidateCompanyForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  const locale = form.context.locale;

  return (
    <VBox gap={"md"}>
      <div>
        <p className={"font-semibold"}>{t("decisions.liquidateCompany.prerequisite.title")}</p>
        <p>{t("decisions.liquidateCompany.prerequisite.dissolution")}</p>
        <p>{t("decisions.liquidateCompany.prerequisite.settlement")}</p>
      </div>
      <Question
        question={t("decisions.liquidateCompany.isDissolutionResolved.questionText")}
        field={form.isDissolutionResolved}
        name={"isDissolutionResolved"}
        noLabel={t("no")}
        isLocked={form.isProjectLocked}
        error={form.dissolutionError}
        finding={
          form.dissolutionGeneralMeetingDate
            ? {
                type: "success",
                content: t("decisions.liquidateCompany.isDissolutionResolved.found", {
                  companyName: form.context.initialState.company.name,
                  date: formatDate(locale, form.dissolutionGeneralMeetingDate),
                }),
              }
            : undefined
        }
      />
      {!form.isDissolutionRejected && (
        <>
          <Question
            question={t("decisions.liquidateCompany.isCreditorNoticeExpired.questionText")}
            field={form.isCreditorNoticeExpired}
            name={"isCreditorNoticeExpired"}
            noLabel={t("no")}
            isLocked={form.isProjectLocked}
            isYesDisabled={form.isCreditorPeriodRunning}
            error={form.creditorError}
            finding={creditorFinding(form, locale, t)}
          />
          <Question
            question={t("decisions.liquidateCompany.isFinalSettlementAudited.questionText")}
            field={form.isFinalSettlementAudited}
            name={"isFinalSettlementAudited"}
            noLabel={t("decisions.liquidateCompany.isFinalSettlementAudited.noAuditor")}
            isLocked={form.isProjectLocked}
            error={form.isFinalSettlementAudited.error}
            finding={
              form.auditorName
                ? {
                    type: "success",
                    content: (
                      <>
                        {t("decisions.liquidateCompany.isFinalSettlementAudited.found", {
                          auditorName: form.auditorName,
                        })}{" "}
                        <a href={`/company/${form.context.orgNo}/settings`}>
                          {t("decisions.liquidateCompany.isFinalSettlementAudited.companySettings")}
                        </a>
                      </>
                    ),
                  }
                : undefined
            }
          />
          <div>
            <p className={"font-semibold"}>
              {t("decisions.liquidateCompany.whatHappensNext.title")}
            </p>
            <p>{t("decisions.liquidateCompany.whatHappensNext.text")}</p>
          </div>
        </>
      )}
    </VBox>
  );
});
